import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { Lock, Unlock } from "lucide-react";

export const useBudgetLock = (year) => {
  const { data } = useQuery({ queryKey: ["budget-lock", year], queryFn: async () => (await api.get(`/budget/lock?year=${year}`)).data, enabled: !!year });
  return data?.locked ? data : null;
};

// Lock/unlock toggle (owner) + banner; locked year rejects every budget write with 423.
export const BudgetLockButton = ({ year }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const lock = useBudgetLock(year);
  const m = useMutation({
    mutationFn: async (locked) => (await api.put("/budget/lock", { year, locked })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["budget-lock", year] }); toast.success(d.locked ? `Ο προϋπολογισμός ${year} κλειδώθηκε` : `Ο προϋπολογισμός ${year} ξεκλειδώθηκε`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (user?.role !== "owner") return lock ? <span data-testid="budget-lock-badge" className="inline-flex items-center gap-1 rounded-xl bg-[#FFF4D6] text-[#92400E] px-3 py-2 text-xs font-semibold"><Lock className="h-3.5 w-3.5" /> Κλειδωμένος</span> : null;
  return (
    <button data-testid="budget-lock-btn" onClick={() => { if (!lock || window.confirm(`Ξεκλείδωμα προϋπολογισμού ${year};`)) m.mutate(!lock); }} disabled={m.isPending}
      title={lock ? `Κλειδώθηκε από ${lock.by} στις ${new Date(lock.at).toLocaleDateString("el-GR")}` : "Κλείδωμα — καμία αλλαγή μέχρι να ξεκλειδωθεί"}
      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border ${lock ? "bg-[#92400E] border-[#92400E] text-white" : "bg-white border-[#E3DED4] text-[#45423C] hover:border-[#45423C]"} disabled:opacity-50`}>
      {lock ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />} {lock ? `Κλειδωμένος ${year}` : `Κλείδωμα ${year}`}
    </button>
  );
};

export const BudgetLockBanner = ({ year }) => {
  const lock = useBudgetLock(year);
  if (!lock) return null;
  return (
    <div data-testid="budget-lock-banner" className="rounded-2xl border border-[#F0C36D] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E] flex items-center gap-2">
      <Lock className="h-4 w-4" /> Ο προϋπολογισμός {year} είναι κλειδωμένος από {lock.by} ({new Date(lock.at).toLocaleDateString("el-GR")}). Οι αλλαγές δεν αποθηκεύονται — ξεκλειδώστε τον από τον Προϋπολογισμό P&L.
    </div>
  );
};
